import { Text } from '@cellula/react';
import { Icon } from '@components/icon.component';
import { Spinner } from '@components/spinner.component';
import { VStack } from '@components/v_stack.component';
import { I18nAndL10n } from '@modules/i18n_and_l10n/mod';
import { withTranslation } from '@modules/i18n_and_l10n/with_translation';
import type { AriaListBoxOptions } from '@react-aria/listbox';
import type { LoadingState, Node } from '@react-types/shared';
import { theme } from '@services/theme.service/theme';
import { rem } from '@visiba/std';
import { UIEvent, useRef } from 'react';
import { useListBox, useOption } from 'react-aria';
import type { ListState } from 'react-stately';
import styled from 'styled-components';
import { BROWSE_KEY, SearchUnitNode } from './_common';

interface Props extends AriaListBoxOptions<unknown> {
	listBoxRef?: React.RefObject<HTMLUListElement>;
	state: ListState<SearchUnitNode>;
	loadingState?: LoadingState;
	onLoadMore?: () => void;
}

const elements = {
	container: styled.div`
		box-shadow:
			0 1px 0 rgb(0 0 0 / 5%),
			0 4px 17px rgb(0 0 0 / 6%);
		min-width: ${rem(480)};
		background: #ffffff;
		border-radius: ${rem(24)};
	`,
	list: styled.ul`
		padding: 0 !important; /* Injection stuff... Remove this later */

		.browse {
			margin-left: ${rem(16)};
		}
	`,
	listItem: styled.li<{ isFocused?: boolean }>`
		position: relative;
		height: ${rem(50)};
		display: flex;
		align-items: center;
		background-color: ${({ isFocused }) => (isFocused ? '#F1F3F8' : null)};
		padding: ${rem(5)};
		/* Adding a mouse pointer since our platform want to treat these as links */
		cursor: pointer;

		&:first-of-type {
			border-top-left-radius: ${rem(24)};
			border-top-right-radius: ${rem(24)};
		}

		&:last-of-type {
			border-bottom-left-radius: ${rem(24)};
			border-bottom-right-radius: ${rem(24)};
		}

		&:last-of-type {
			&::after {
				content: none;
			}
		}

		&.noMouse {
			cursor: default;
		}
	`,
	iconContainer: styled.div`
		margin: 0 ${rem(20)} 0 ${rem(20)};
		height: ${rem(20)};
		width: ${rem(20)};
		font-size: ${rem(20)};
		display: flex;
		color: rgba(120, 140, 152, 0.5);
	`,
	icon: styled.span`
		margin-left: ${rem(10)};
		vertical-align: middle;
	`,
};

export function ListBox(props: Props): React.JSX.Element {
	const ref = useRef<HTMLUListElement>(null);
	const { listBoxRef = ref, state } = props;
	const { listBoxProps } = useListBox(props, state, listBoxRef);

	function onScroll(event: UIEvent): void {
		const scrollOffset = event.currentTarget.scrollHeight - event.currentTarget.clientHeight * 2;
		if (event.currentTarget.scrollTop > scrollOffset && props.onLoadMore) {
			props.onLoadMore();
		}
	}

	return (
		<elements.container>
			<elements.list {...listBoxProps} ref={listBoxRef} onScroll={onScroll}>
				{[...state.collection].filter((item) => item.value?.id !== BROWSE_KEY).length === 0 ? <NoListableReceptions /> : null}

				{[...state.collection].map((item) => (
					<Option key={item.key} item={item} state={state} />
				))}

				{props.loadingState === 'loadingMore' && (
					// Display a spinner at the bottom of the list if we're loading more.
					// role="option" is required for valid ARIA semantics since
					// we're inside a role="listbox".
					// biome-ignore lint/a11y/useFocusableInteractive: suppressed due to migration - should be fixed
					<div role='option' aria-selected='false'>
						<Spinner />
					</div>
				)}
			</elements.list>
		</elements.container>
	);
}

interface OptionProps {
	item: Node<SearchUnitNode>;
	state: ListState<unknown>;
}

function Option({ item, state }: OptionProps): JSX.Element {
	const ref = useRef<HTMLLIElement>(null);
	const { optionProps, isFocused } = useOption(
		{
			key: item.key,
		},
		state,
		ref,
	);

	if (item.value?.id === BROWSE_KEY) {
		return (
			<elements.listItem {...optionProps} ref={ref} isFocused={isFocused} role='link'>
				<elements.iconContainer />
				<Text variant='bodyRegular' color={theme.hyperLinkDefault}>
					{withTranslation('Patient.DefaultHomeGuiComponents.Browse')}
					<elements.icon aria-hidden='true'>
						<Icon type='arrowRight' />
					</elements.icon>
				</Text>
			</elements.listItem>
		);
	}

	return (
		<elements.listItem {...optionProps} ref={ref} isFocused={isFocused} role='link'>
			<elements.iconContainer>
				<Icon type='home2' />
			</elements.iconContainer>
			<VStack fluid={false}>
				<Text variant='bodyRegular'>{item.rendered}</Text>
				{item.value?.subtitle ? <Text variant='bodySmallRegular'>{item.value?.subtitle}</Text> : null}
			</VStack>
		</elements.listItem>
	);
}

function NoListableReceptions(): JSX.Element {
	return (
		// role="option" is required for valid ARIA semantics since
		// we're inside a role="listbox".
		<elements.listItem
			role='option'
			aria-selected='false'
			className='noMouse'
			aria-label={I18nAndL10n.translate('PW.Component.SearchReceptionCB.NoResult')}
		>
			<elements.iconContainer />
			<Text aria-hidden='true' variant='bodyRegular'>
				{withTranslation('PW.Component.SearchReceptionCB.NoResult')}
			</Text>
		</elements.listItem>
	);
}
